import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { HorizontalRevenueBar } from "@/components/Charts";
import { fmtCurrency, fmtNumber, fmtPct } from "@/lib/format";
import { SummaryBanner, SectionBox, EmptyBlock } from "@/components/DashboardBits";

export default function RevenueIntelligence({ analysis }) {
  const k = analysis.kpis || {};
  const payers = analysis.payer_mix || [];
  const topDept = analysis.top_departments_by_revenue?.[0];

  const summary = topDept
    ? `${topDept.name} leads with ${fmtCurrency(topDept.revenue)} from ${fmtNumber(
        topDept.volume
      )} cases. ${
        k.claims_leakage_pct !== null
          ? `Claims leakage runs at ${fmtPct(k.claims_leakage_pct)} of billed revenue.`
          : ""
      }`
    : "Where revenue is generated across departments, procedures, and payers.";

  return (
    <div className="space-y-8">
      <SummaryBanner title="Where Money Is Made" text={summary} testId="revenue-summary-banner" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionBox title="Revenue by Department" eyebrow="/ Service lines" testId="rev-by-dept">
          {analysis.revenue_by_department?.length ? (
            <HorizontalRevenueBar data={analysis.revenue_by_department.slice(0, 10)} color="#5E3BFF" />
          ) : (
            <EmptyBlock text="No department data." />
          )}
        </SectionBox>

        <SectionBox title="Revenue by Procedure" eyebrow="/ Top 10" testId="rev-by-proc">
          {analysis.revenue_by_procedure?.length ? (
            <HorizontalRevenueBar data={analysis.revenue_by_procedure} color="#5E3BFF" />
          ) : (
            <EmptyBlock text="No procedure data." />
          )}
        </SectionBox>
      </div>

      {payers.length ? (
        <SectionBox
          title="Payer Mix & Claims Leakage"
          eyebrow="/ Where reimbursement breaks"
          testId="payer-mix-section"
          description="Each payer's share, realized revenue, and denial rate. Leakage = billed minus collected."
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {payers.map((p, i) => (
              <div
                key={p.name}
                className="border border-gray-200 p-5"
                data-testid={`payer-card-${i}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">
                    / {p.name}
                  </div>
                  <div className="font-mono-metric text-xs text-gray-400">
                    {fmtPct(p.share_pct, 0)}
                  </div>
                </div>
                <div className="font-display text-xl font-black tracking-tighter text-gray-950">
                  {fmtCurrency(p.revenue)}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Volume</span>
                    <span className="font-mono-metric">{fmtNumber(p.volume)}</span>
                  </div>
                  {p.denial_rate_pct !== undefined && p.denial_rate_pct !== null && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Denial rate</span>
                      <span
                        className={`font-mono-metric ${
                          p.denial_rate_pct > 10 ? "text-[#DC2626]" : "text-gray-900"
                        }`}
                      >
                        {fmtPct(p.denial_rate_pct)}
                      </span>
                    </div>
                  )}
                  {p.realization_pct !== undefined && p.realization_pct !== null && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Realization</span>
                      <span
                        className={`font-mono-metric ${
                          p.realization_pct < 80 ? "text-[#DC2626]" : "text-green-700"
                        }`}
                      >
                        {fmtPct(p.realization_pct, 0)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionBox>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionBox
          title="High-volume, Low-margin Services"
          eyebrow="/ Opportunity"
          description="Procedures you run often but earn thin margins on. Worth repricing, renegotiating supplies, or re-routing capacity."
          testId="high-vol-low-margin"
        >
          {analysis.high_vol_low_margin_procedures?.length ? (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200">
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                    Procedure
                  </TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                    Volume
                  </TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                    Margin %
                  </TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                    Revenue
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analysis.high_vol_low_margin_procedures.map((p) => (
                  <TableRow key={p.name} className="border-b border-gray-100">
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-right font-mono-metric">
                      {fmtNumber(p.volume)}
                    </TableCell>
                    <TableCell className="text-right font-mono-metric text-[#DC2626]">
                      {p.margin_pct !== null && p.margin_pct !== undefined
                        ? fmtPct(p.margin_pct)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono-metric">
                      {fmtCurrency(p.revenue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyBlock text="No high-volume low-margin procedures identified." />
          )}
        </SectionBox>

        <SectionBox
          title="High-margin, Underutilized Services"
          eyebrow="/ Growth"
          description="Procedures with strong margins but low volume — where scaling is almost pure upside."
          testId="high-margin-underused"
        >
          {analysis.high_margin_underused_procedures?.length ? (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200">
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                    Procedure
                  </TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                    Volume
                  </TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                    Margin %
                  </TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                    Revenue
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analysis.high_margin_underused_procedures.map((p) => (
                  <TableRow key={p.name} className="border-b border-gray-100">
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-right font-mono-metric">
                      {fmtNumber(p.volume)}
                    </TableCell>
                    <TableCell className="text-right font-mono-metric text-green-700">
                      {p.margin_pct !== null && p.margin_pct !== undefined
                        ? fmtPct(p.margin_pct)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono-metric">
                      {fmtCurrency(p.revenue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyBlock text="Need cost/procurement data to compute margins." />
          )}
        </SectionBox>
      </div>
    </div>
  );
}
